import * as React from "react";
import { AccountCircle, Help, Logout, MoreVert } from "@mui/icons-material";
import WorkspacesFilledIcon from "@mui/icons-material/Workspaces";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Link,
  Paper,
  Typography,
  Popover,
  styled,
} from "@mui/material";
import { checkTenantOptionsFromCookies, logout } from "../../../api_calls/requestConfig";
import ResetPassword from "./Modal/ResetPassword";
import WorkSpace from "../../../pages/Login/modalContent/WorkSpace";
import TenantSelection from "../../../pages/Login/modalContent/TenantSelection";
import { accessOptions, checkAccessTypeFromCookie, checkUserDetailsFromCookie, checkWorkSpaceFromCookies, switchToBrandAccess, switchWorkspace } from "../../../api_calls/userManagement";
import {
  HtmlTooltipLightRed,
  HtmlTooltipLightWhite,
  StyledInformationSpan,
  StyledLightRedInformationTypography,
  StyledMainBox,
} from "../StyledComponents/styled";
import LogoRed from "../../../images/cyreen_vertical.png";
import SingleSelect from "../Selectors/SingleSelect";
import { sortSelectorOptions } from "../Selectors/selectorOptionUtils";
import RetailerSelectionFilter from "../Filters/RetailerSelectionFilter";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getBackendVersion } from "../../../api_calls/utils";
import { userTypeCanUseRetailerSelection } from "../../../api_calls/retailerSelection";

// Brand Access stays first. The remaining workspaces still sort by label.
const orderAccessSelectionOptions = (options) => {
  const accessOptionsList = Array.isArray(options) ? options : [];
  const brandAccessOption = accessOptionsList.find((option) => option.value === "brandAccess");
  const otherOptions = sortSelectorOptions(
    accessOptionsList.filter((option) => option.value !== "brandAccess")
  );

  return typeof brandAccessOption !== "undefined" ? [brandAccessOption, ...otherOptions] : otherOptions;
};

const LogoImage = styled("img")({
  maxWidth: "100%",
  height: "auto",
  paddingLeft: "0",
  paddingTop: "15px",
  paddingRight: "15px",
  paddingBottom: "15px",
});

export default function BottomNav({ open, accessFor }) {
  const currentStateData = useSelector((state) => state.userSelections);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isWorkSpaceModalOpen, setWorkSpaceModal] = React.useState(false);
  const [isTenantModalOpen, setTenantModal] = React.useState(false);
  const [showWorkSpaceDropUp, setShowWorkSpaceDropUp] = React.useState(false);
  const [workSpaceName, setWorkSpaceName] = React.useState("");
  const [backendVersion, setBackendVersion] = React.useState('Loading...');
  const [retailerSelectionOptions, setRetailerSelectionOptions] = React.useState([]);
  const [selectedRetailer, setSelectedRetailer] = React.useState({});

  const accessSelectionOptions = orderAccessSelectionOptions(accessOptions());
  const cookieTenantOptions = checkTenantOptionsFromCookies();
  const tenantSelectionOptions = typeof currentStateData.tenant_options !== "undefined" ? currentStateData.tenant_options : cookieTenantOptions.tenant_options;

  const [selectedWorkSpace, setSelectedWorkSpace] = React.useState({});
  const [userType, setUserType] = React.useState("");

  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const userAccessType = typeof userType.user_type !== "undefined" ? userType.user_type.toLowerCase() : "";
  const tenantOptionsWithoutBrand = tenantSelectionOptions.filter((tenantOption) => !["brand", "brand access"].includes(tenantOption.access_type));
  const tenantIds = [...new Set(tenantOptionsWithoutBrand.map((tenantOption) => tenantOption.tenant_id))];
  const hasBrandAccess = tenantSelectionOptions.some((tenantOption) => ["brand", "brand access"].includes(tenantOption.access_type));
  const hasTenantSelection = tenantIds.length > 1 || (tenantIds.length === 1 && hasBrandAccess === true);
  const showTenantSelectionIcon = hasTenantSelection === true;
  const showRetailerSelection = userTypeCanUseRetailerSelection(userAccessType);
  const showAccessSelection = workSpaceName !== "" && showRetailerSelection === false;

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "simple-popover" : undefined;

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    const fetchBackendVersion = async () => {
      const version = await getBackendVersion();
      setBackendVersion(version);
    };

    fetchBackendVersion();
  }, []);

  const navIcons = () => {
    return (
      <>
        {/* ----------- about --------- */}
        <HtmlTooltipLightWhite
          disableFocusListener={true}
          title={
            <Box width={240}>
              <StyledMainBox>
                <Typography variant="h6" sx={{ color: "#F04B55" }}>
                  <strong>CAP Explorer</strong>
                </Typography>

                <Box height={10} />
                <Typography component="p" variant="body3">Version: 5.6.17 ({backendVersion})</Typography>
                <Box height={10} />
                <Typography component="p" variant="body3">
                  {
                    typeof accessFor !== "undefined" && accessFor === "preview" ?
                      <Typography component="span" variant="body3" sx={{ color: "#62626E" }}>Report Problem</Typography>
                      : <Link color="inherit" href="mailto:support@cyreen.de?subject=CAP Explorer - Report a Problem">Report Problem</Link>
                  }

                </Typography>
                <Box height={10} />
                <Typography component="p" variant="body3">
                  {
                    typeof accessFor !== "undefined" && accessFor === "preview" ?
                      <Typography component="span" variant="body3" sx={{ color: "#62626E" }}>Help Center</Typography>
                      : <Link color="inherit" target="_blank" rel="noreferrer" href="https://help.cyreen.de/en">Help Center</Link>
                  }

                </Typography>
                <Box height={15} />
                <LogoImage src={LogoRed} alt="Cyreen" width={"100"} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  Developed and maintained by:
                </Typography>
                <Box height={10} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  Cyreen GmbH
                </Typography>
                <Box height={2} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  Schaumainkai 91
                </Typography>
                <Box height={2} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  Frankfurt am Main
                </Typography>
                <Box height={2} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  Germany
                </Typography>
                <Box height={2} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  {<Link color="inherit" target="_blank" rel="noreferrer" href="https://cyreen.de">
                    www.cyreen.de
                  </Link>
                  }
                </Typography>
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  {<Link color="inherit" target="_blank" rel="noreferrer" href="/third-party-notices">
                    Third-Party Notice
                  </Link>
                  }
                </Typography>
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  {<Link color="inherit" target="_blank" rel="noreferrer" href="https://cyreen.de/imprint">
                    Impressum
                  </Link>
                  }
                </Typography>
                <Box height={12} />
                <Typography component="p" variant="caption" sx={{ fontSize: "10px" }}>
                  CAP Explorer, Cyreen and Cyreen logos are copyright protected.
                </Typography>
              </StyledMainBox>
            </Box>
          }
        >
          <BottomNavigationAction
            onClick={(e) => {
              e.preventDefault();
            }}
            sx={{ color: "rgba(240, 75, 85, 0.89)", minWidth: "65px" }}
            label="User"
            icon={<Help />}
          />
        </HtmlTooltipLightWhite>
        {/* -------- logout ------- */}
        <HtmlTooltipLightRed title="Logout">
          <BottomNavigationAction
            onClick={(e) => {

              if (typeof accessFor !== "undefined" && accessFor === "preview") {
                e.preventDefault();
              } else {
                e.preventDefault();
                logout();
              }
            }}
            sx={{ color: "rgba(240, 75, 85, 0.89)", minWidth: "65px" }}
            label="User"
            icon={<Logout />}
          />
        </HtmlTooltipLightRed>
        {/* ------- account settings --------- */}
        <HtmlTooltipLightRed title="Account Settings">
          <BottomNavigationAction
            onClick={(e) => {

              if (typeof accessFor !== "undefined" && accessFor === "preview") {
                e.preventDefault();
              } else {
                e.preventDefault();
                openModal(true);
              }
            }}
            sx={{ color: "rgba(240, 75, 85, 0.89)", minWidth: "65px" }}
            label="User"
            icon={<AccountCircle />}
          />
        </HtmlTooltipLightRed>

        {/* ------- tenant selection --------- */}
        {showTenantSelectionIcon === true && (
          <HtmlTooltipLightRed title="Tenant Selection">
            <BottomNavigationAction
              onClick={(e) => {
                if (typeof accessFor !== "undefined" && accessFor === "preview") {
                  e.preventDefault();
                } else {
                  e.preventDefault();
                  setTenantModal(true);
                }
              }}
              sx={{ color: "rgba(240, 75, 85, 0.89)", minWidth: "65px" }}
              label="Tenant Selection"
              icon={<WorkspacesFilledIcon />}
            />
          </HtmlTooltipLightRed>
        )}

        {/* ------- work space change option ----------- */}
        {/* {showWorkSpaceIcon && (
          <HtmlTooltipLightRed title="Switch Workspace">
            <BottomNavigationAction
              onClick={(e) => {
                if (typeof accessFor !== "undefined" && accessFor === "preview") {
                  e.preventDefault();
                } else {
                  e.preventDefault();
                  setWorkSpaceModal(true);
                }
              }}
              sx={{ color: "rgba(240, 75, 85, 0.89)", minWidth: "65px" }}
              label="WorkSpace"
              icon={<Workspaces />}
            />
          </HtmlTooltipLightRed>
        )} */}
      </>
    );
  };

  React.useEffect(() => {
    const checkWorkSpaces = checkWorkSpaceFromCookies();
    const checkUserDetails = checkUserDetailsFromCookie();
    const checkAccessTypes = checkAccessTypeFromCookie();

    if (checkWorkSpaces.success === true) {
      if (checkWorkSpaces.workspace_options.length > 1 || (checkAccessTypes.success === true && checkAccessTypes.access_types.has_multiple_access === true)) {
        setShowWorkSpaceDropUp(true);
      }

      if (
        typeof checkWorkSpaces.current_workspace !== "undefined" &&
        typeof checkWorkSpaces.current_workspace.id !== "undefined"
      ) {

        // if user role == brand then set brand access as selected option
        if (checkUserDetails.success === true && checkUserDetails.user_detail.user_role === "Brand") {
          setWorkSpaceName("Brand Access");
          setSelectedWorkSpace({ label: "Brand Access", value: "brandAccess" });
        } else {
          setWorkSpaceName(checkWorkSpaces?.current_workspace?.name);
          setSelectedWorkSpace({ label: checkWorkSpaces.current_workspace.name, value: checkWorkSpaces.current_workspace.id });
        }
      }

    }
    if (checkUserDetails.success === true) {
      setUserType(checkUserDetails.user_detail);
    }
  }, []);

  return (
    <>
      {/* ----------- bottom navigation ------------ */}
      <Paper sx={{ position: "fixed", width: open ? 260 : 63, bottom: 0, left: 0, right: 0 }} elevation={2}>

        {/* --------- retailer selection ----------- */}
        {open && showRetailerSelection === true && (
          <Box sx={{ display: "flex", justifyContent: "center", px: 2, pt: 1, pb: 0.5 }}>
            <RetailerSelectionFilter
              elemId="bottom_nav_retailer_selection"
              filterSelectionReduxKey="bottom_nav_selected_retailer"
            />
          </Box>
        )}

        {/* --------------- access/workspace selection ------------ */}
        {open && showAccessSelection === true && (
          <StyledLightRedInformationTypography
            variant="body2"
            style={{ textAlign: "center", justifyContent: "center", marginLeft: "0px" }}
          >
            {
              showWorkSpaceDropUp === true ? (
                <SingleSelect
                  enableFullWidth={false}
                  elemId={"workspace_options"}
                  label="Access Selection"
                  options={accessSelectionOptions}
                  defaultVal={selectedWorkSpace}
                  selectionCheck={(option, value) => option.value === value.value}
                  disableSearch={true}
                  sortOptions={false}
                  flip={true}
                  onChangeCall={(selectedOption) => {
                    if (selectedOption.value === selectedWorkSpace.value) return;

                    if (selectedOption.value === "brandAccess") {
                      switchToBrandAccess(navigate, "update");
                    } else {
                      switchWorkspace(selectedOption.value, navigate, "update");
                    }
                  }}
                />
              ) : (
                <StyledInformationSpan style={{ fontSize: "15px", overflowWrap: "anywhere" }}>
                  {workSpaceName}
                </StyledInformationSpan>
              )
            }
          </StyledLightRedInformationTypography>
        )}

        <Box height={6} />

        {open ? (
          <BottomNavigation sx={{ backgroundColor: "rgba(240, 75, 85, 0.09)" }}>
            {navIcons()}
          </BottomNavigation>
        ) : (
          <BottomNavigation sx={{ backgroundColor: "rgba(240, 75, 85, 0.09)" }}>
            <BottomNavigationAction
              aria-describedby={id}
              onClick={handleClick}
              sx={{ color: "rgba(240, 75, 85, 0.89)" }}
              label="Popover"
              icon={<MoreVert />}
            />
            <Popover
              id={id}
              open={openPopover}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "center",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: "left",
              }}
              sx={{ marginTop: "0.8em" }}
            >
              {/* -------- POPOVER CONTENT ------- */}
              {navIcons()}
            </Popover>
          </BottomNavigation>
        )}
      </Paper>
      {/* ============ modal popup for reset password ========== */}
      {isModalOpen && <ResetPassword open={isModalOpen} onClose={closeModal} size={"xs"} />}
      {/* -------- change workspace modal -------- */}
      {isWorkSpaceModalOpen === true && (
        <WorkSpace
          open={isWorkSpaceModalOpen}
          onClose={() => {
            setWorkSpaceModal(false);
          }}
          size={"xs"}
          usedFor={"update"}
        />
      )}
      {isTenantModalOpen === true && (
        <TenantSelection
          open={isTenantModalOpen}
          onClose={() => {
            setTenantModal(false);
          }}
          onWorkspaceSelectionRequired={() => {
            setWorkSpaceModal(true);
          }}
          usedFor={"update"}
        />
      )}
    </>
  );
}
